import React, { useState } from 'react';
import BubbleForm, { BubbleFormPage } from '../BubbleUI/BubbleForm';
import SelectContentPage from './SelectContentPage';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import ModifyContentDetailsPage from './ModifyContentDetailsPage';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { Content } from '@go-shared/types/content';
import { Plan } from '@go-shared/types/plan';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { EventFormDayTransfer } from '../CanvasEventForm/CanvasEventForm';
import { ContentFormikMode } from '../Formik/ContentFormik';
import { ContentTemplateTranslation } from '@go-shared/types/translation';

export interface CanvasContentFormMode {
  type: 'create' | 'edit' | 'filter';
  content?: Content;
  initialPage?: number;
  dayTransfer?: EventFormDayTransfer;
}

interface CanvasContentFormProps {
  mode: CanvasContentFormMode;
  plan: Plan;
  contentTemplates: BareContentTemplate[];
  venueTemplates: VenueTemplate[];
  contentTemplateTranslations: ContentTemplateTranslation[];
  onCreateTemplate?: (template: BareContentTemplate) => void;
  onRemoveContent?: (contentId: string) => void;
  onClose?: () => void;
  onSubmitContent?: (content: Content, transferToTargetDay?: string) => void;
}

export default function CanvasContentForm(props: CanvasContentFormProps) {
  const { user } = useUserInfo();
  const { brand } = useServerInfo();
  const { activeProperty: property } = useActiveProperty();

  const [selectedTemplate, setSelectedTemplate] = useState<BareContentTemplate | undefined>(
    props.mode.content?.template
  );

  function handleCreateTemplate(template: BareContentTemplate) {
    if (props.onCreateTemplate) {
      props.onCreateTemplate(template);
      setSelectedTemplate(template);
    }
  }

  const pages: BubbleFormPage<unknown>[] = [];

  function modeConverter(mode: CanvasContentFormMode): ContentFormikMode {
    const { content } = mode;
    return {
      ...mode,
      type: mode.type === 'create' ? 'create' : 'edit',
      edit: content,
    };
  }

  pages.push(
    {
      key: 'page-1',
      render: actions => (
        <SelectContentPage
          user={user}
          brand={brand}
          property={property}
          mode={props.mode}
          title={`${props.mode.type === 'create' ? 'Add' : 'Edit'} Content`}
          contentTemplates={props.contentTemplates}
          venueTemplates={props.venueTemplates}
          selected={selectedTemplate}
          onSelect={setSelectedTemplate}
          onCreate={handleCreateTemplate}
          onClose={props.onClose}
          onGoNext={actions.goForward}
        />
      ),
    },
    {
      key: 'page-2',
      render: actions =>
        selectedTemplate && (
          <ModifyContentDetailsPage
            mode={modeConverter(props.mode)}
            title={`${props.mode.type === 'create' ? 'Add' : 'Edit'} Content`}
            submitTitle={`Finish: ${props.mode.type === 'create' ? 'Create' : 'Edit'} Content`}
            plan={props.plan}
            selectedCntTemplate={selectedTemplate}
            contentTemplateTranslations={props.contentTemplateTranslations}
            venueTemplates={props.venueTemplates}
            onDelete={props.onRemoveContent}
            onClose={props.onClose}
            onGoBack={actions.goBack}
            onSubmit={props.onSubmitContent}
          />
        ),
    }
  );

  return (
    <div className="canvas-content-form">
      <BubbleForm pages={pages} startAtPage={props.mode.initialPage} />
    </div>
  );
}
